import { LucideIcon } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { usePathname, useRouter } from "next/navigation";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

type NavSecondaryProps = {
  items: {
    title: string;
    url: string;
    className?: string;
    pages?: {
      title: string;
      label?: React.ReactNode;
      url: string;
      exactMatch?: boolean;
      icon: LucideIcon;
      iconClassName?: string;
      badge?: number;
      statusBadge?: { label: string; className: string };
    }[];
  }[];
} & React.ComponentProps<typeof SidebarGroup>;

export function NavSecondary({ items }: NavSecondaryProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile, isMobile } = useSidebar();

  return (
    <>
      {items.map((item) => (
        <SidebarGroupContent
          key={item.title}
          className={`px-2 pb-4 pt-2 ${item.className}`}
        >
          <SidebarGroupLabel className="font-medium text-muted">
            {item.title}
          </SidebarGroupLabel>
          <SidebarMenu>
            {item.pages?.map((page) => (
              <SidebarMenuItem key={page.title}>
                <SidebarMenuButton
                  variant="custom"
                  onClick={() => {
                    if (isMobile) setOpenMobile(false);
                    if (page.url.includes("http")) {
                      window.open(page.url, "_blank");
                    } else {
                      startNavigationFeedback(page.url);
                      router.push(page.url);
                    }
                  }}
                  isActive={
                    page.exactMatch
                      ? pathname === page.url
                      : pathname.includes(page.url)
                  }
                  className="px-2.5 font-medium md:px-1.5"
                >
                  <page.icon
                    className={`text-muted ${["Discord", "PowerOn Pro", "PowerOnPro", "PowerOnPipelines"].includes(page.icon.displayName || "") && "fill-muted"} ${page.iconClassName ?? ""}`}
                  />
                  <span>{page.label ?? page.title}</span>
                </SidebarMenuButton>
                {page.statusBadge && (
                  <SidebarMenuBadge
                    aria-label={`Tax application: ${page.statusBadge.label}`}
                    className={`rounded-full px-2 py-0 text-[10px] font-semibold uppercase leading-none ${page.statusBadge.className}`}
                  >
                    {page.statusBadge.label}
                  </SidebarMenuBadge>
                )}
                {page.badge ? (
                  <SidebarMenuBadge className="min-w-7 rounded-full bg-red-500/15 px-2 pb-0.5 font-semibold leading-none !text-red-500">
                    {page.badge}
                  </SidebarMenuBadge>
                ) : null}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      ))}
    </>
  );
}
